import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import { adminApi } from "../../lib/adminApi";
import { toast } from "sonner";
import { ArrowLeft, ScanLine, Sprout, Trash2 } from "lucide-react";

const TIERS = ["free", "premium", "platinum"];

export default function UserDetailScreen() {
  const { userId } = useParams();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await adminApi.get(`/users/${userId}`);
      setD(data);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const changeTier = async (tier) => {
    setBusy(true);
    try {
      await adminApi.put(`/users/${userId}/tier`, { tier });
      toast.success(`Tier set to ${tier}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggleSuspend = async () => {
    setBusy(true);
    try {
      await adminApi.put(`/users/${userId}/suspend`, { suspended: !d.user?.suspended });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const deleteUser = async () => {
    if (!window.confirm("Permanently delete this user and all their data?")) return;
    try {
      await adminApi.delete(`/users/${userId}`);
      toast.success("User deleted");
      navigate("/admin/users");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!d) {
    return <AdminLayout><div className="p-10 text-center"><span className="inline-block w-3 h-3 rounded-full bg-[#4CAF50] dot-pulse" /></div></AdminLayout>;
  }

  const u = d.user;

  return (
    <AdminLayout>
      <button onClick={() => navigate("/admin/users")} className="flex items-center gap-1 text-[#4CAF50] text-sm mb-3 hover:underline">
        <ArrowLeft size={14} /> Users
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 glass-card-strong p-6" data-testid="user-detail-card">
          <div className="flex items-center gap-4 mb-5">
            {u.picture ? (
              <img src={u.picture} className="w-16 h-16 rounded-full" alt="" />
            ) : (
              <div className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold"
                   style={{ background: "rgba(76,175,80,0.18)", color: "#4CAF50" }}>
                {u.name?.[0]?.toUpperCase() || "U"}
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold">{u.name}</h1>
              <p className="text-soft text-sm">{u.email}</p>
              <p className="text-[11px] text-muted-soft mt-1">{u.country || "No country"}</p>
            </div>
            <span className={`ml-auto glass-pill ${u.tier === "platinum" ? "purple" : u.tier === "premium" ? "amber" : ""}`}>
              {u.tier}
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: "Scans", value: d.scan_count, icon: ScanLine },
              { label: "Tracked plants", value: d.journal_count, icon: Sprout },
              { label: "Provider", value: u.auth_provider },
              { label: "Suspended", value: u.suspended ? "Yes" : "No" },
            ].map((m, i) => (
              <div key={i} className="rounded-lg p-3"
                   style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
                <p className="text-[10px] uppercase tracking-wider text-muted-soft">{m.label}</p>
                <p className="text-base font-bold mt-0.5">{m.value}</p>
              </div>
            ))}
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-2">Change tier</p>
            <div className="flex gap-2 flex-wrap">
              {TIERS.map((t) => (
                <button
                  key={t}
                  onClick={() => changeTier(t)}
                  disabled={busy || u.tier === t}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold capitalize transition ${
                    u.tier === t
                      ? "bg-[rgba(76,175,80,0.18)] border border-[rgba(76,175,80,0.5)] text-white"
                      : "border border-white/15 text-white/70 hover:text-white hover:border-white/40"
                  }`}
                  data-testid={`set-tier-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="glass-card p-5 space-y-3">
          <p className="text-[11px] uppercase tracking-wider text-muted-soft">Actions</p>
          <button onClick={toggleSuspend} disabled={busy} className="btn-outline w-full" data-testid="suspend-user">
            {u.suspended ? "Reinstate user" : "Suspend user"}
          </button>
          <button onClick={deleteUser} className="w-full rounded-xl text-red-400 border border-red-500/30 hover:bg-red-500/10 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2" data-testid="delete-user">
            <Trash2 size={14} /> Delete account
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
